import { useEffect, useRef, useState } from 'react'

const createObserver = (cb?: () => void, threshold = 0.5) => {
  if ('IntersectionObserver' in window) {
    return new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          cb?.()
        }
      },
      { threshold },
    )
  }
  return null
}

export const useInView = (
  cb?: () => void,
  threshold = 0.5,
): {
  ref: (instance: Element | null) => void
  disconnect: () => void
  unobserve: () => void
} => {
  const [ref, setRef] = useState<Element | null>(null)
  const observer = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    if (ref && cb) {
      const instance = observer.current
      if (instance) {
        instance.observe(ref)

        return () => instance.unobserve(ref)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref])

  useEffect(() => {
    if (cb) {
      const instance = createObserver(cb, threshold)
      observer.current = instance
      if (ref && instance) {
        instance.observe(ref)

        return () => instance.disconnect()
      }
    } else if (observer.current && !cb) {
      observer.current.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cb, threshold])

  return {
    ref: setRef,
    disconnect: () => observer.current?.disconnect(),
    unobserve: () => ref && observer.current?.unobserve(ref),
  }
}
